/**
 * Safely translate a position value that may be stored as:
 * - A translation key (e.g. "founder", "ceo")
 * - A display string (e.g. "Founder", "CEO/President")
 * - A comma-separated string of old values (e.g. "Founder, Partner")
 * - An array of keys or display strings
 *
 * Uses existsFn to check key existence before calling translateFn,
 * preventing i18next missingKey warnings.
 */
export function translatePosition(
  position: string | string[] | undefined | null,
  translateFn: (key: string) => string,
  keyPrefix: string = "positionOptions",
  existsFn?: (key: string) => boolean
): string {
  if (!position) return "";

  const translateSingle = (val: string): string => {
    const trimmed = val.trim();
    if (!trimmed) return "";

    const key = `${keyPrefix}.${trimmed}`;

    // If we have an exists checker, skip translation for missing keys (avoids i18next warnings)
    if (existsFn && !existsFn(key)) {
      return trimmed;
    }

    const translated = translateFn(key);

    // If the translation function returns the key itself, no match exists.
    if (translated === key || translated === trimmed) {
      return trimmed;
    }
    return translated;
  };

  // Handle a value that may itself contain commas
  const translateValue = (val: string): string => {
    const trimmed = val.trim();
    if (!trimmed) return "";

    // Split comma-separated values (e.g. "CEO/President, Owner")
    if (trimmed.includes(",")) {
      return trimmed
        .split(",")
        .map((part) => translateSingle(part.trim()))
        .filter(Boolean)
        .join(", ");
    }

    return translateSingle(trimmed);
  };

  if (Array.isArray(position)) {
    return position.map(translateValue).filter(Boolean).join(", ");
  }

  return translateValue(position);
}
